<template>
  <div>
    <div class="d-flex align-center">
      <v-text-field
        v-model="search"
        label="Search by ID, invoice #, customer, or purpose"
        prepend-inner-icon="mdi-magnify"
        clearable
      ></v-text-field>
      <v-select
        v-model="sortBy"
        :items="sortOptions"
        label="Sort by"
        class="ml-4"
        style="max-width: 180px"
        dense
        hide-details
      ></v-select>
      <v-btn
        icon
        class="ml-2"
        :title="sortDesc ? 'Descending' : 'Ascending'"
        @click="sortDesc = !sortDesc"
      >
        <v-icon>{{
          sortDesc ? "mdi-sort-descending" : "mdi-sort-ascending"
        }}</v-icon>
      </v-btn>
      <v-btn icon color="gray" @click="refresh" :loading="loading">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>
    <v-data-table
      :search="search"
      :custom-filter="invoiceSearchFilter"
      :items="deleted_invoices"
      :headers="deleted_invoices_headers"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :loading="loading"
    >
      <template v-slot:item.purpose="{ item }">
        <div v-if="item.purpose" class="purpose-cell" :title="item.purpose">
          {{ item.purpose }}
        </div>
        <span v-else class="grey--text">—</span>
      </template>
      <template v-slot:item.action="{ item }">
        <v-btn
          small
          color="primary"
          outlined
          :loading="restoringId === item.id"
          :disabled="restoringId !== null"
          @click="revert(item)"
        >
          <v-icon left small>mdi-undo-variant</v-icon>
          Revert
        </v-btn>
      </template>
    </v-data-table>
    <p class="caption grey--text text-right">deletedInvoicesTable.vue</p>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  invoiceSearchFilter,
  invoiceSortOptions,
} from "@/utils/invoiceSearchFilter";
export default {
  data() {
    return {
      search: "",
      loading: false,
      restoringId: null,
      sortBy: "id",
      sortDesc: true,
      sortOptions: invoiceSortOptions,
      deleted_invoices_headers: [
        { text: "ID", value: "id" },
        { text: "Invoice Code", value: "invoice_code" },
        { text: "Customer Name", value: "customer.customer_name" },
        { text: "Invoice Date", value: "invoice_date" },
        { text: "Total Amount", value: "total_amount" },
        { text: "Purpose", value: "purpose", width: 240 },
        { text: "Deleted At", value: "deleted_at" },
        { text: "Actions", value: "action", sortable: false },
      ],
    };
  },
  methods: {
    invoiceSearchFilter,
    ...mapActions({
      get_deleted_invoices: "invoice/get_deleted_invoices",
      restore_invoice: "invoice/restore_invoice",
      get_all_invoices: "invoice/get_all_invoices",
    }),
    async refresh() {
      this.loading = true;
      try {
        await this.get_deleted_invoices();
      } finally {
        this.loading = false;
      }
    },
    async revert(item) {
      const confirmed = await this.$confirm({
        title: "Revert Deleted Invoice",
        message:
          "Restore invoice " +
          (item.invoice_code || item.id) +
          "? This un-deletes the invoice and its payments, reverses the deletion's journal entries, and re-deducts item stock.",
        color: "primary",
        icon: "mdi-undo-variant",
      });
      if (!confirmed) return;

      // Symmetric to delete: for a paid/partially-paid invoice, the deletion only
      // returned stock if the user said so. Ask whether to deduct it again.
      // Fully unpaid invoices always had stock returned, so the server re-deducts
      // automatically (no prompt needed).
      let rededuct_stock = false;
      const needsAsk =
        item.invoice_status && item.invoice_status !== "Waiting for Payment";
      if (needsAsk) {
        rededuct_stock = window.confirm(
          "This invoice was paid/partially paid.\n\n" +
            "When it was deleted, were the item(s) returned to stock?\n\n" +
            "OK = Yes, deduct them from stock again\n" +
            "Cancel = No, keep stock as-is"
        );
      }

      this.restoringId = item.id;
      try {
        await this.restore_invoice({ id: item.id, rededuct_stock });
        // Bring it back into the All Invoices list too.
        await this.get_all_invoices();
        this.$alert({
          title: "Restored",
          message: "Invoice restored successfully.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        const data = e.response && e.response.data;
        let msg = (data && (data.error || data.message)) || e.message;
        // Insufficient-stock error returns a per-item list
        if (data && data.items && data.items.length) {
          msg =
            "Cannot restore — insufficient stock:\n" +
            data.items
              .map(
                (it) =>
                  "• " +
                  it.item_name +
                  " (need " +
                  it.requested +
                  ", have " +
                  it.available +
                  ")"
              )
              .join("\n");
        }
        this.$alert({
          title: "Error",
          message: msg,
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.restoringId = null;
      }
    },
  },
  computed: {
    ...mapGetters({
      deleted_invoices: "invoice/deleted_invoices",
    }),
  },
  created() {
    if (this.deleted_invoices.length == 0) this.refresh();
  },
};
</script>

<style lang="scss" scoped>
.purpose-cell {
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
